import type { KpiOption } from '../config/kpiCatalog';
import type { PlanMetricMetricRow, PlanMetricRow } from '../types';

/**
 * Restores backend catalog order (`metrics[].order` /
 * `promo_metric_grid.requestable_kpis[].order`) on a KPI selection.
 *
 * The toolbar's multi-select reports values in the order the user clicked them,
 * which would otherwise leak into the Promo Metric column order, the
 * `/report/period-data` request body, the export payload and the query key. Keys
 * missing from the catalog are appended instead of dropped, so a
 * catalog/selection mismatch surfaces as a stray column rather than as data
 * that silently disappears.
 *
 * Callers must pass the catalog that owns the selection: `kpiCatalog` for Plan
 * Metrics, `promoKpiCatalog` for Promo Metrics.
 */
export function orderKpisByCatalog(
    selected: readonly string[],
    catalog: readonly KpiOption[],
): string[] {
    const selectedKeys = new Set(selected);
    const ordered = catalog.flatMap((kpi) => (selectedKeys.has(kpi.key) ? [kpi.key] : []));
    if (ordered.length === selectedKeys.size) return ordered;
    const known = new Set(ordered);
    return [...ordered, ...[...selectedKeys].filter((key) => !known.has(key))];
}

const isMetricRow = (row: PlanMetricRow): row is PlanMetricMetricRow =>
    typeof (row as PlanMetricMetricRow).kpi_id === 'string';

/**
 * Re-imposes catalog order on Plan Metric (`view_type: metric`) rows. The
 * `/report/period-data` payload is a list of KPI rows whose sequence is the
 * backend's, not `metrics[].order`. Rows without a `kpi_id` or whose key is
 * absent from the catalog are appended so they stay visible.
 *
 * Skip this when the user has sorted a column — that order is intentional and
 * already applied server-side.
 */
export function orderMetricRowsByCatalog(
    rows: readonly PlanMetricRow[],
    catalog: readonly KpiOption[],
): PlanMetricRow[] {
    const byId = new Map<string, PlanMetricRow>();
    const leftover: PlanMetricRow[] = [];
    for (const row of rows) {
        if (isMetricRow(row) && row.kpi_id) byId.set(row.kpi_id, row);
        else leftover.push(row);
    }
    const ordered = orderKpisByCatalog([...byId.keys()], catalog).map((id) => byId.get(id)!);
    return leftover.length === 0 ? ordered : [...ordered, ...leftover];
}
